import React, { useEffect, useState, useRef } from "react";
import { Theme } from "@mui/material/styles";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import InvoiceTable from "../Components/InvoiceTable";
import InvoiceActions from "../Components/InvoiceActions";
import { useSnackbar } from "notistack";
import { DataStore } from "aws-amplify/datastore";
import { Invoice } from "../models";
import EmailTextConfirm from "../Components/EmailTextConfirm";
import InvoiceLabelDialog from "../Components/InvoiceLabelDialog";
import { useRepositories } from "../repositories";
import { ClientRecord, InvoiceRecord, InvoiceStatus } from "../repositories/types";
import { isE2EMode } from "../e2eMode";

interface InvoicesProps {}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      "flex-wrap": "wrap",
      "justify-content": "center",
      "flex-direction": "column",
    },
    dateSelect: {
      margin: "2em",
    },
  })
);

const Invoices: React.FC<InvoicesProps> = () => {
  const classes = useStyles();
  const { clients: clientRepo, invoices: invoiceRepo } = useRepositories();
  const [clientInvoices, setClientInvoices] = useState<InvoiceRecord[]>([]);
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const today = new Date();
  const [startDate, setStartDate] = useState<Date | null>(new Date(today.getFullYear(), today.getMonth(), 1));
  const [endDate, setEndDate] = useState<Date | null>(new Date(today.getFullYear(), today.getMonth() + 1, 0));
  const prevStartDate = useRef<Date | null>(null);
  const [emailConfirmOpen, setEmailConfirmOpen] = useState(false);
  const [invoiceLabelOpen, setInvoiceLabelOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [selectedInvoices, setSelectedInvoices] = useState<InvoiceRecord[]>([]);

  const fetchClientData = React.useCallback(async () => {
    const [clientData, clientList] = await Promise.all([
      clientRepo.loadForInvoicing(startDate ?? new Date(), endDate ?? new Date()),
      clientRepo.list(),
    ]);
    setClients(clientList);
    setClientInvoices(clientData);
  }, [startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (startDate && endDate && startDate > endDate && prevStartDate.current !== startDate) {
      setEndDate(startDate);
    }
    if (startDate && endDate && startDate > endDate && prevStartDate.current === startDate) {
      setStartDate(endDate);
    }
    prevStartDate.current = startDate;
    fetchClientData();
  }, [startDate, endDate, fetchClientData]);

  const update = async () => {
    setClientInvoices(await clientRepo.loadForInvoicing(startDate ?? new Date(), endDate ?? new Date()));
  };

  useEffect(() => {
    // In e2e mode there is no DataStore sync to observe — the created
    // invoice is picked up by the next loadForInvoicing fetch instead.
    if (isE2EMode) return;
    const subscription = DataStore.observe(Invoice).subscribe((msg) => {
      if (msg.opType === "INSERT" || msg.opType === "UPDATE") {
        const element = msg.element as any;
        const updatedRecord: InvoiceRecord = {
          id: element.id,
          clientID: element.clientID,
          invoiceName: element.invoiceName ?? "",
          filePath: element.filePath,
          startDate: element.startDate,
          endDate: element.endDate,
          subtotal: element.subtotal,
          owner: element.owner,
          label: element.label,
          status: element.status ?? null,
          servicesRendered: [],
        };

        setClientInvoices((prev) => {
          if (msg.opType === "INSERT") {
            return prev.map((i) =>
              i.clientID === element.clientID &&
              i.startDate === element.startDate &&
              i.status === InvoiceStatus.CREATING
                ? updatedRecord
                : i
            );
          }
          return prev.map((i) => (i.id === element.id ? updatedRecord : i));
        });
      }
    });
    return () => subscription.unsubscribe();
  });

  const deleteInvoices = async () => {
    const selectedIds = selectedInvoices.map((i) => i.id);
    const cleared = clientInvoices
      .filter((i) => selectedIds.includes(i.id))
      .map((c) => ({ ...c, status: null, filePath: null, invoiceName: "" }));

    setClientInvoices([...clientInvoices.filter((i) => !selectedIds.includes(i.id)), ...cleared]);
    await Promise.all(selectedInvoices.map((i) => invoiceRepo.delete(i.id)));
    setSelectedInvoices([]);
  };

  const sendInvoices = async (message: string, subject: string) => {
    const selectedIds = selectedInvoices.map((i) => i.id);
    const sending = clientInvoices
      .filter((i) => selectedIds.includes(i.id))
      .map((c) => ({ ...c, status: InvoiceStatus.SENDING }));
    setClientInvoices([...clientInvoices.filter((i) => !selectedIds.includes(i.id)), ...sending]);
    setEmailConfirmOpen(false);

    invoiceRepo.send(selectedIds, message, subject).catch(() => {
      enqueueSnackbar("Failed to send selected invoices", { variant: "error" });
      const reverted = clientInvoices
        .filter((i) => selectedIds.includes(i.id))
        .map((c) => ({ ...c, status: InvoiceStatus.CREATED }));
      setClientInvoices([...clientInvoices.filter((i) => !selectedIds.includes(i.id)), ...reverted]);
    });
    setSelectedInvoices([]);
  };

  const createInvoices = async (label?: string) => {
    setInvoiceLabelOpen(false);
    const selectedIds = selectedInvoices.map((i) => i.id);
    const clientIds = Array.from(
      new Set(clientInvoices.filter((i) => selectedIds.includes(i.id)).map((i) => i.clientID))
    );
    const creating = clientInvoices
      .filter((i) => selectedIds.includes(i.id))
      .map((c) => ({ ...c, status: InvoiceStatus.CREATING, label: label }));
    setClientInvoices([...clientInvoices.filter((i) => !selectedIds.includes(i.id)), ...creating]);

    invoiceRepo.create(startDate ?? new Date(), endDate ?? new Date(), clientIds, label ?? "").catch(() => {
      enqueueSnackbar("Failed to create invoices", { variant: "error" });
      const reverted = clientInvoices
        .filter((i) => selectedIds.includes(i.id))
        .map((c) => ({ ...c, status: undefined }));
      setClientInvoices([...clientInvoices.filter((i) => !selectedIds.includes(i.id)), ...reverted]);
    });
    setSelectedInvoices([]);
  };

  const markInvoicesAs = (status: InvoiceStatus, paidAt?: string) => {
    const selectedIds = selectedInvoices.map((i) => i.id);
    const changed = clientInvoices
      .filter((i) => selectedIds.includes(i.id))
      .map((c) => ({ ...c, status, ...(paidAt ? { paidAt } : {}) }));
    setClientInvoices([...clientInvoices.filter((i) => !selectedIds.includes(i.id)), ...changed]);

    changed.forEach((i) => {
      if (status === InvoiceStatus.VOIDING) {
        invoiceRepo.cancel(i).then(() => update());
      } else {
        invoiceRepo.update(i).catch(() => enqueueSnackbar("Failed to update invoice", { variant: "error" }));
      }
    });
    setSelectedInvoices([]);
  };

  return (
    <div className={classes.root}>
      <div className={classes.dateSelect}>
        <DatePicker format="dd/MM/yyyy" label="Start Date" closeOnSelect value={startDate} onChange={setStartDate} />
        <DatePicker format="dd/MM/yyyy" label="End Date" closeOnSelect value={endDate} onChange={setEndDate} />
      </div>
      <InvoiceActions
        createInvoices={() => setInvoiceLabelOpen(true)}
        sendInvoices={() => setEmailConfirmOpen(true)}
        clients={clients}
        selectedInvoices={selectedInvoices}
        deleteInvoices={deleteInvoices}
        markInvoicesAs={markInvoicesAs}
      />
      {clientInvoices && clientInvoices.length > 0 && (
        <InvoiceTable
          clients={clients}
          clientInvoices={clientInvoices}
          update={update}
          setSelectedInvoices={setSelectedInvoices}
          selectedInvoices={selectedInvoices}
        />
      )}
      <EmailTextConfirm
        open={emailConfirmOpen}
        confirmFunction={sendInvoices}
        rejectFunction={() => setEmailConfirmOpen(false)}
      />
      <InvoiceLabelDialog
        open={invoiceLabelOpen}
        confirmFunction={createInvoices}
        rejectFunction={createInvoices}
        multiple={selectedInvoices.length > 1}
      />
    </div>
  );
};

export default Invoices;
